import type { OfferData } from './hooks/useOfferActions';
import { MissingField } from './types';

/**
 * Reading the error a Send Offer request actually failed with (AXHE-4234).
 *
 * `useApiServiceMutation` hands `onError` the PARSED body of the `ApiError` it
 * caught (`e.body`), and every failure mode of the offer endpoints carries a
 * `message` in it: the 422s (unresolved merge fields, a terminal-status offer,
 * an applicant not at an offer stage, unresolvable signers, an invalid
 * placement) and the 403s alike — see InternalApi::OfferLettersController's
 * `rescue_from` handlers. Discarding it, as this flow used to, collapsed all of
 * them into one generic sentence, which is why a screenshot of the failure told
 * nobody — recruiter or engineer — what had gone wrong.
 */

/**
 * The server-supplied message for a failed request, or `undefined` when the
 * failure carried none.
 *
 * `undefined` is deliberately narrow: it means we genuinely have nothing to
 * say — the request never reached a handler (network error), was cancelled, or
 * came back as something other than the JSON error envelope (an HTML 500 page,
 * a proxy error). Only then is the caller's generic copy the honest thing to
 * show. A body we could read but whose `message` is blank counts as nothing
 * too.
 *
 * Deliberately NOT stringifying an arbitrary body: a non-JSON error response
 * body arrives as a raw string (an HTML error page, a proxy's plain-text
 * message), and surfacing that would put markup — or `[object Object]` for a
 * body of some other shape — in front of a recruiter. Only the documented
 * `{ message }` envelope is trusted.
 */
/**
 * The per-field problems a 422 carried, or `[]` when it carried none we can
 * trust (AXHE-4441 review).
 *
 * `UnresolvedMergeKeysError`'s handler returns `missing_fields` alongside
 * `message` (`offer_letters_controller.rb:64`) — the SAME entries the readiness
 * check reports, so the same splitter can read them. It is worth reading here
 * because `message` is the exception's own string: *"Offer letter has 1
 * unresolved merge field(s): SIGNATURE_HIRING_MANAGER (unresolvable_signer)"*.
 * That is written for an engineer — a reason token and a merge key — and the
 * handler's own preamble says so. `missing_fields` is the structured half of the
 * same refusal, and it is what lets the client say the sentence a recruiter can
 * act on instead.
 *
 * Narrow for the reason `serverErrorMessage` is: only the documented envelope is
 * trusted. Entries are validated one at a time rather than the array being cast,
 * because a partially-shaped body is exactly what would put `undefined` into the
 * copy. `reason` is not checked against the enum here — an unknown reason is a
 * field problem to every caller, which is the safe default and the same one
 * `normalize.ts` takes.
 */
export const serverMissingFields = (error: unknown): MissingField[] => {
  if (error == null || typeof error !== 'object' || error instanceof Error) return [];

  const { missing_fields: fields } = error as { missing_fields?: unknown };
  if (!Array.isArray(fields)) return [];

  return fields.flatMap(entry => {
    if (entry == null || typeof entry !== 'object') return [];

    const { key, reason } = entry as { key?: unknown; reason?: unknown };
    if (typeof key !== 'string' || typeof reason !== 'string') return [];

    return [{ key, reason: reason as MissingField['reason'] }];
  });
};

export const serverErrorMessage = (error: unknown): string | undefined => {
  if (error == null || typeof error !== 'object') return undefined;
  // An Error also has a `.message`, but it is a client-side/transport string
  // ("Network request failed", "Failed to fetch") written for a developer, not
  // copy the server chose to show a recruiter. `useApiServiceMutation` only
  // ever passes a parsed body here, so this is belt-and-braces — but the
  // difference matters enough not to leave it to the caller.
  if (error instanceof Error) return undefined;

  const { message } = error as { message?: unknown };
  if (typeof message !== 'string') return undefined;

  return message.trim() || undefined;
};

/**
 * The offer a create refused because it is already in flight, or `undefined`.
 *
 * The create endpoint answers 409 with `{ message, preview }` when the
 * applicant's newest offer for the template waits on an approver, waits to be
 * sent, or went out and can still be canceled. Only that refusal carries
 * `preview` on create, so its presence is the signal. An `id` string is the
 * least the read-only view needs.
 */
export const serverInFlightOffer = (error: unknown): OfferData | undefined => {
  // A thrown Error is a transport failure, never the parsed refusal body.
  if (error == null || error instanceof Error) return undefined;

  const { preview } = error as { preview?: { id?: unknown } | null };
  if (typeof preview?.id !== 'string') return undefined;

  return preview as OfferData;
};
